// StuckBar (job page): once the job's header has scrolled away, a slim bar stays at the top of the
// sheet with the job's name, where it stands (its StateMark and word, and what's owed or left to
// clear), History, Top and the same main button. It slides in (no motion for prefers-reduced-motion)
// and, while it's hidden, is inert, so nothing in it can be reached. Reads only --stuck-*.
import { useEffect, useState, type ReactNode } from 'react';
import { StateMark, type MarkState } from '../../components/StateMark/StateMark.tsx';
import './StuckBar.css';

export interface StuckBarProps {
  title: string;
  /** Round 2 leaves it out: the state word alone, as the StateLine says it. */
  mark?: MarkState;
  word: string;
  /** After the word: "3 to clear" (a Link), "$3,322.00 owing". */
  extra?: ReactNode;
  /** History, Top, and the main button. */
  actions: ReactNode;
  /** The header: the bar shows once it has scrolled out of sight. */
  watch?: { readonly current: Element | null };
  /** Storybook: shown without scrolling. */
  isShown?: boolean;
}

export function StuckBar({ title, mark, word, extra, actions, watch, isShown }: StuckBarProps) {
  const [gone, setGone] = useState(false);
  useEffect(() => {
    const el = watch?.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) =>
      setGone(!!e && !e.isIntersecting && e.boundingClientRect.top < 0),
    );
    io.observe(el);
    return () => io.disconnect();
  }, [watch]);
  const on = isShown ?? gone;
  return (
    <div className="fl-stuck">
      <div
        className="fl-stuck__bar"
        data-on={on ? '' : undefined}
        inert={!on}
        role="region"
        aria-label={`${title}, at the top`}
      >
        <span className="fl-stuck__title">{title}</span>
        <span className="fl-stuck__state">
          {mark ? <StateMark state={mark} /> : null}
          <b className="fl-stuck__word">{word}</b>
          {extra ? <span className="fl-stuck__extra">· {extra}</span> : null}
        </span>
        <span className="fl-stuck__acts">{actions}</span>
      </div>
    </div>
  );
}
